import {Injectable, Injector} from "@angular/core";

import {ReportError} from "app/app.service";
import {APP_DOMAIN_CONTEXT_TOKEN} from "app/services/domain/app-domain-context.token";
import type {AppDomainContext} from "app/services/domain/domain.module";
import {TagBaselineDefinitionVersionExtended, TagBaselineDefinitionVersionsService} from "app/services/domain/tag-baseline-versions.service";
import {CloudAccountSummaryListRequest} from "app/services/domain/tag-posture-cloud-account-summary.service";
import {TagPostureResourceSummaryBatchRequest} from "app/services/domain/tag-posture-resource-summary.service";
import {FilterOptionsExtended} from "app/services/domain/filter-options/filter-options.service";
import {WorkloadDefinitionExtended} from "app/services/domain/workload-definitions.service";
import * as Models from "app/services/proxy/model/models";
import {TagRemediationHandler} from "app/tag-governance/remediation/tag-remediation-handler";

import {Lookup, LookupHelpers, UtilsService} from "framework/services/utils.service";
import {ChartColorUtilities, ColorGradientStop, StepwiseColorMapper} from "framework/ui/charting/core/colors";
import {BaseComponent} from "framework/ui/components";
import {ControlOption} from "framework/ui/control-option";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {PercentagePipe} from "framework/ui/formatting/string-format.pipe";
import {Future, mapInParallelNoNulls} from "framework/utils/concurrency";
import {Memoizer, ResetMemoizers} from "framework/utils/memoizers";

@Injectable()
export class TagPostureService
{
    private cloudAccountSummariesBySnapshot: Lookup<Models.TagPostureCloudAccountSummary[]> = {};
    private cloudSummariesBySnapshot: Lookup<TagGovernanceData>                             = {};
    public workloadSummariesBySnapshot: Lookup<TagGovernanceData>                           = {};
    public workloadCoverageCounts: ResourceCoverage;
    public selectedCloud: Models.CloudServiceName;
    private m_domain: AppDomainContext;
 
    constructor(private readonly m_injector: Injector)
    {
    }

    get domain(): AppDomainContext
    {
        if(!this.m_domain)
        {
            this.m_domain = this.m_injector.get(APP_DOMAIN_CONTEXT_TOKEN);
        }
        return this.m_domain
    }

    @Memoizer
    public async mostRecentSnapshot(): Promise<Models.TagPostureSnapshot>
    {
        const snapshotInfo = await this.getSnapshotInfo();
        for (let snapshot of snapshotInfo || [])
        {
            if (snapshot.status === Models.TagPostureSnapshotStatus.Success)
            {
                return snapshot;
            }
        }
        return null;
    }

    @Memoizer
    public async snapshotsAvailable(): Promise<boolean>
    {
        const snapshotInfo = await this.getSnapshotInfo();
        return snapshotInfo?.length > 0;
    }

    public async getSnapshotPosture(snapshotId: string): Promise<Models.TagPostureSnapshot>
    {
        const snapshotInfo = await this.getSnapshotInfo();

        return snapshotInfo.find((s) => s.snapshot_id === snapshotId);
    }

    public async getFailedSnapshotCount(): Promise<number>
    {
        const snapshotInfo = await this.getSnapshotInfo();
        return snapshotInfo?.filter(s => s.status === Models.TagPostureSnapshotStatus.Failed).length ?? 0;
    }

    public async getRunningSnapshot(): Promise<Models.TagPostureSnapshot>
    {
        const userId  = this.domain.users.userId;
        const running = await this.domain.tagPostureSnapshot.getAll(
            Models.TagPostureSnapshotListRequest.newInstance({
                                                                 filters: Models.TagPostureSnapshotFilters.newInstance({
                                                                                                                           status: [Models.TagPostureSnapshotStatus.Running]
                                                                                                                       })
                                                             }));

        return running?.map(([id, r]) => r)
                      .filter((r) => r.created_by === userId)[0];
    }

    public async getOverallCoverage(snapshotId: string): Promise<TagCoverageSummary>
    {
        snapshotId ||= await this.ensureReady(snapshotId);
        return this.cloudSummariesBySnapshot[snapshotId]?.overallCoverage || this.workloadSummariesBySnapshot[snapshotId]?.overallCoverage;
    }

    public async getOrganizationCoverages(snapshotId: string): Promise<TagCoverageSummary[]>
    {
        snapshotId ||= await this.ensureReady(snapshotId);
        return this.cloudSummariesBySnapshot[snapshotId]?.organizationCoverages || [];
    }

    public async getTenantCoverages(snapshotId: string): Promise<TagCoverageSummary[]>
    {
        snapshotId ||= await this.ensureReady(snapshotId);
        return this.cloudSummariesBySnapshot[snapshotId]?.tenantCoverages || [];
    }

    public async getCloudAccountCoverages(snapshotId: string): Promise<TagCoverageSummary[]>
    {
        snapshotId ||= await this.ensureReady(snapshotId);
        return this.cloudSummariesBySnapshot[snapshotId]?.cloudAccountCoverages || [];
    }

    public async getWorkloadCoverages(snapshotId: string): Promise<TagCoverageSummary[]>
    {
        snapshotId = await this.getWorkloadInfo(snapshotId);
        return this.workloadSummariesBySnapshot[snapshotId]?.cloudAccountCoverages || [];
    }

    //--//

    public async batchResourcePostures(snapshot: Models.TagPostureSnapshot,
                                       request: Models.TagPostureResourceBatchRequest): Promise<Models.TagPostureResourceBatchResponse>
    {
        snapshot ||= await this.mostRecentSnapshot();
        return this.domain.apis.tagPosturesApi.resourceBatch(snapshot.snapshot_id, request);
    }

       public async getResourceSummaryBatch(snapshot: Models.TagPostureSnapshot,
                                         ids: string[]): Promise<Lookup<Models.TagPostureResourceSummary>>
    {
        snapshot ||= await this.mostRecentSnapshot();
        const batchRequest = new TagPostureResourceSummaryBatchRequest(snapshot.snapshot_id, ids);
        return this.domain.tagPostureResourceSummary.batchAsLookup(batchRequest);
    }

    public getRemediationStatusFromSummary(cloudAccountCoverageSummary: TagCoverageSummary,
                                           resourceIds: string[]): Promise<Models.BulkTagRemediationStatusResponse>
    {
        return this.getRemediationStatus(resourceIds, cloudAccountCoverageSummary.cloudAccountId);
    }

    public getRemediationStatus(resourceIds: string[],
                                serviceAccountId: string): Promise<Models.BulkTagRemediationStatusResponse>
    {
        const batchRequest = Models.BulkTagRemediationStatusRequest.newInstance({
                                                                                    service_account_id: serviceAccountId,
                                                                                    resource_ids      : resourceIds
                                                                                });

        return this.domain.apis.resource.checkBulkTagRemediationStatus(batchRequest);
    }

    @ReportError
    public executeTagRemediation(remediationRequest: Models.BulkTagRemediationRequest): Promise<Models.BulkTagRemediationResponse>
    {
        return this.domain.apis.resource.createBulkTagRemediation(remediationRequest);
    }

    public async waitForPostureGeneration(component: BaseComponent): Promise<boolean>
    {
        let postureRunningStatus: boolean = !!await this.getRunningSnapshot();

        while (postureRunningStatus && !component?.wasDestroyed())
        {
            postureRunningStatus = !!await this.getRunningSnapshot();

            if (!postureRunningStatus)
            {
                this.reset();
                return true;
            }
            else
            {
                await Future.delayed(5000);
            }
        }

        this.reset();
        return !component?.wasDestroyed();
    }

    //--//

    @ResetMemoizers
    public reset()
    {
        this.cloudAccountSummariesBySnapshot = {};
        this.cloudSummariesBySnapshot        = {};
        this.workloadSummariesBySnapshot     = {};
    }

    // todo: also provide organizationId, tenantId, and cloudAccountId and make cloudAccountSummariesBySnapshot partially fillable
    private async ensureTagInfo(snapshot: Models.TagPostureSnapshot)
    {
        const snapshotId    = snapshot.snapshot_id;
        const filterOptions = await this.getFilterOptions();
        // Ensure cloud/tenant/account summaries are present regardless of workload caches
        if (!this.cloudSummariesBySnapshot[snapshotId])
        {
            const summaryFilters = Models.TagPostureContext.newInstance({filter: Models.AccountFilters.newInstance({clouds: []})});
            const summaryReq     = Models.TagPostureSummaryListRequest.newInstance({
                                                                                       filters: summaryFilters
                                                                                   });
            const listRequest    = new CloudAccountSummaryListRequest(snapshotId, summaryReq);

            const summaries = await this.domain.tagPostureCloudAccountSummary.getAll(listRequest);

            const summaryValues = summaries?.map(([id, summary]) => summary);

            const summaryValuesArray = summaryValues?.map(value => new TagPostureCloudAccountSummaryExtended(value));

            this.cloudAccountSummariesBySnapshot[snapshotId] = summaryValues;
            this.cloudSummariesBySnapshot[snapshotId]        = new TagGovernanceData(snapshot, filterOptions, summaryValuesArray);
        }
    }

    @Memoizer
    private async getSnapshotInfo()
    {
        const snapshots = await this.domain.tagPostureSnapshot.getAll(
            Models.TagPostureSnapshotListRequest.newInstance({}));
        return snapshots?.map(([id, snapshot]) => snapshot);
    }

    private async getWorkloadInfo(snapshotId: string): Promise<string>
    {
        const batchResponse = await this.getWorkloadBatch();
        const snapshot      = await this.mostRecentSnapshot();
        snapshotId ||= snapshot.snapshot_id;

        const summaryData: WorkloadSummary[] = await mapInParallelNoNulls(batchResponse, async (item) =>
        {
            const cloudSummary = await this.postureWorkloadSummary(snapshotId, item.sysId);
            return cloudSummary?.results[item.sysId]?.workload_cloud_summary.length > 0 ? {
                definition    : item,
                postureSummary: cloudSummary.results[item.sysId].workload_cloud_summary
            } : null;
        });
        this.coverageSummaryForWorkloadView(summaryData);
        await this.ensureWorkloadInfo(snapshot, summaryData);
        return snapshot?.snapshot_id;
    }

    private coverageSummaryForWorkloadView(workloadSummaries: WorkloadSummary[]): void
    {
        this.workloadCoverageCounts = this.initializeCoverageCounts();

        if (workloadSummaries.length === 0)
        {
            return;
        }

        this.workloadCoverageCounts = workloadSummaries.reduce((counts,
                                                                summary) =>
                                                               {
                                                                   for (const posture of summary.postureSummary ?? [])
                                                                   {
                                                                       counts.resource_count += posture.resource_coverage?.resource_count ?? 0;
                                                                       counts.valid_resource_count += posture.resource_coverage?.valid_resource_count ?? 0;

                                                                       for (const tag of posture.tag_coverage ?? [])
                                                                       {
                                                                           counts.tag_counts.valid_count += tag.resource_counts?.valid_count ?? 0;
                                                                           counts.tag_counts.invalid_count += tag.resource_counts?.invalid_count ?? 0;
                                                                           counts.tag_counts.missing_keys_count += tag.resource_counts?.missing_keys_count ?? 0;
                                                                       }
                                                                   }
                                                                   return counts;
                                                               }, this.initializeCoverageCounts());
    }

    private initializeCoverageCounts(): ResourceCoverage
    {
        return {
            valid_resource_count: 0,
            resource_count      : 0,
            tag_counts          : {
                valid_count       : 0,
                invalid_count     : 0,
                missing_keys_count: 0
            }
        };
    }


    private postureWorkloadSummary(snapshotId: string,
                                   id: string)
    {
        return this.domain.apis.tagPosturesApi.tagPostureWorkloadSummary(snapshotId, id);
    }

    public async getWorkloadList(): Promise<Models.ListResponse>
    {
        const options = await this.getFilterOptions();

        const availableClouds: Models.CloudServiceName[] = Array.from(
            new Set(
                options.filterOptions.tenants.flatMap(tenant => tenant.cloud_providers.map(provider => provider.cloud))
            )
        );

        if (availableClouds.length)
        {
            const queryOperator = Models.WorkloadQueryCloud.newInstance({
                                                                            clouds: availableClouds
                                                                        });

            const filters = Models.WorkloadFilter.newInstance({
                                                                  query: queryOperator
                                                              });

            const listRequest = Models.WorkloadDefinitionListRequest.newInstance({filters: filters});
            return await this.domain.apis.workloadDefinitionsApi.list(listRequest);
        }
        return null;
    }

    public async getWorkloadBatch(): Promise<Array<Models.WorkloadDefinition>>
    {
        const workloadListIds = await this.getWorkloadList();

        const batchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: workloadListIds.results.map(id => WorkloadDefinitionExtended.newIdentity(id))});

        const batchResponse = await this.workloadDefinitionBatch(batchRequest);

        return batchResponse.filter(item => item.is_active);
    }

    private async workloadDefinitionBatch(request: Models.RecordIdentityBatchRequest): Promise<Array<Models.WorkloadDefinition>>
    {
        return await this.domain.apis.workloadDefinitionsApi.batch(request);
    }

    private async ensureWorkloadInfo(snapshot: Models.TagPostureSnapshot,
                                     workloadSummaries: WorkloadSummary[])
    {
        const snapshotId    = snapshot.snapshot_id;
        const filterOptions = await this.getFilterOptions();

        const summaryExtended                            = workloadSummaries.map((data) => new TagPostureCloudAccountSummaryExtended(data.postureSummary[0], data.definition, data.postureSummary));
        this.cloudAccountSummariesBySnapshot[snapshotId] = workloadSummaries.map(data => data.postureSummary[0]);
        this.workloadSummariesBySnapshot[snapshotId]     = new TagGovernanceData(snapshot, filterOptions, summaryExtended);
    }

    @Memoizer
    private getFilterOptions(): Promise<FilterOptionsExtended>
    {
        return this.domain.filterOptions.getFilterOptions();
    }

    private async ensureReady(snapshotId: string): Promise<string>
    {
        let snapshot: Models.TagPostureSnapshot;
        if (snapshotId)
        {
            const snapshots = await this.getSnapshotInfo();
            snapshot        = snapshots.find((s) => s.snapshot_id === snapshotId);
        }
        snapshot ||= await this.mostRecentSnapshot();
        if (snapshot) await this.ensureTagInfo(snapshot);

        return snapshot?.snapshot_id;
    }

    public async fetchCoverage(request: ScoreRequestContext, tag_key?: string): Promise<Models.TagPostureResourceCoverage>
    {
        const accountFilters = Models.AccountFilters.newInstance({
                                                                     clouds        : [],
                                                                     cloud_accounts: request.cloudAccountId ? [request.cloudAccountId] : []
                                                                 });

        const filters = Models.TagPostureContext.newInstance({filter: accountFilters});
        if (tag_key)
        {
            filters.tag_key = tag_key;
        }

        if (request.searchText)
        {
            filters.text_query = request.searchText;
        }

        if (request.workloadId)
        {
            filters.workload_id = request.workloadId;
        }

        const requestFilters = Models.TagPostureContext.newInstance({
                                                                        ...filters,
                                                                        ...request.query
                                                                    });

        const filter = Models.TagPostureSummaryListRequest.newInstance({
                                                                           filters: requestFilters
                                                                       });

        return await this.domain.apis.tagPosturesApi.resourceSummary(request.snapshotId, filter);
    }
}

class TagGovernanceData
{
    public readonly overallCoverage                             = new TagCoverageSummary(this.snapshot, this.m_filterOptions);
    public readonly organizationCoverages: TagCoverageSummary[] = [];
    public readonly tenantCoverages: TagCoverageSummary[]       = [];
    public readonly cloudAccountCoverages: TagCoverageSummary[] = [];

    constructor(public readonly snapshot: Models.TagPostureSnapshot,
                private readonly m_filterOptions: FilterOptionsExtended,
                accountSummaries: TagPostureCloudAccountSummaryExtended[])
    {
        const coverageByTenant: Lookup<TagCoverageSummary> = {};
        if (!accountSummaries) accountSummaries = [];
        for (const accountSummary of accountSummaries)
        {
            const cloudAccountCoverageSummary = new TagCoverageSummary(this.snapshot, this.m_filterOptions, accountSummary.summaryRow, accountSummary.workloadRow);
            cloudAccountCoverageSummary.processCloudAccountSummary(accountSummary, false);
            this.cloudAccountCoverages.push(cloudAccountCoverageSummary);

            const tenantId            = cloudAccountCoverageSummary.tenantId;
            let tenantCoverageSummary = coverageByTenant[tenantId];
            if (!tenantCoverageSummary)
            {
                tenantCoverageSummary = new TagCoverageSummary(this.snapshot, this.m_filterOptions);

                coverageByTenant[tenantId] = tenantCoverageSummary;
                this.tenantCoverages.push(tenantCoverageSummary);
            }

            tenantCoverageSummary.processCoverageSummary(cloudAccountCoverageSummary, false);
        }

        const coverageByOrganization: Lookup<TagCoverageSummary> = {};
        for (let tenantId in coverageByTenant)
        {
            const tenantCoverageSummary = coverageByTenant[tenantId];

            const accountId                 = tenantCoverageSummary.accountId;
            let organizationCoverageSummary = coverageByOrganization[accountId];
            if (!organizationCoverageSummary)
            {
                organizationCoverageSummary = new TagCoverageSummary(this.snapshot, this.m_filterOptions);

                coverageByOrganization[accountId] = organizationCoverageSummary;
                this.organizationCoverages.push(organizationCoverageSummary);
            }

            organizationCoverageSummary.processCoverageSummary(tenantCoverageSummary, false);
        }

        for (let organizationId in coverageByOrganization)
        {
            this.overallCoverage.processCoverageSummary(coverageByOrganization[organizationId], false);
        }
    }
}

export class TagCoverageSummary
{
    private static readonly COLOR_MAPPER = new StepwiseColorMapper([
                                                                       new ColorGradientStop(0, ChartColorUtilities.getColorById("Severity Colors", "critical").hex),
                                                                       new ColorGradientStop(30, ChartColorUtilities.getColorById("Severity Colors", "high").hex),
                                                                       new ColorGradientStop(79, ChartColorUtilities.getColorById("Severity Colors", "low").hex)
                                                                   ]);
    public static readonly COLOR_FN      = (value: number) => TagCoverageSummary.COLOR_MAPPER.getColor(value);

    private m_validTags: number = 0;
    get validTags(): number
    {
        return this.m_validTags;
    }

    private m_numTags: number = 0;
    get numTags(): number
    {
        return this.m_numTags;
    }

    get tagCoveragePercent(): number
    {
        return PercentagePipe.computeSafelyClamped(this.m_validTags, this.m_numTags);
    }

    private m_coveredResources: number = 0;
    get coveredResources(): number
    {
        return this.m_coveredResources;
    }

    private m_numResources: number = 0;
    get numResources(): number
    {
        return this.m_numResources;
    }

    get resourceCoveragePercent(): number
    {
        return PercentagePipe.computeSafelyClamped(this.m_coveredResources, this.m_numResources);
    }

    private m_cloudCoverages = new Map<Models.CloudServiceName, TagCoverageSummary>();

    private m_associatedClouds = new Set<Models.CloudServiceName>();
    get associatedClouds(): Set<Models.CloudServiceName>
    {
        return this.m_associatedClouds;
    }

    private m_postureScopeInfo = new TagScopeInfo();

    get accountId(): string
    {
        return this.m_postureScopeInfo.organizationId;
    }

    get tenantId(): string
    {
        return this.m_postureScopeInfo.tenantId;
    }

    private m_tenantOption: ControlOption<string>;

    get tenantName(): string
    {
        return this.m_tenantOption?.label;
    }

    get workloadName(): string
    {
        return this.m_postureScopeInfo.workloadName;
    }

    get workloadId(): string
    {
        return this.m_postureScopeInfo?.workloadId;
    }

    get cloudAccountId(): string
    {
        return this.m_postureScopeInfo.cloudAccountId;
    }

    get cloudAccountName(): string
    {
        return this.m_postureScopeInfo.cloudAccountName;
    }

    private m_postureTime: string;
    get postureTime(): string
    {
        return this.m_postureTime;
    }

    private m_processedSummaries = new Map<string, Models.TagPostureCloudAccountSummary>();
    get processedSummaries(): Models.TagPostureCloudAccountSummary[]
    {
        if (this?.summaryRow?.length > 0)
        {
            this.m_processedSummaries = this.summaryRow.reduce((acc,
                                                                item) =>
                                                               {
                                                                   if (!acc.has(item.cloud_account_id))
                                                                   {
                                                                       acc.set(item.cloud_account_id, item);
                                                                   }
                                                                   return acc;
                                                               }, new Map<string, Models.TagPostureCloudAccountSummary>());
        }
        return Array.from(this.m_processedSummaries.values());
    }

    get hasData(): boolean
    {
        return !!this.m_processedSummaries.size;
    }

    constructor(public readonly snapshot: Models.TagPostureSnapshot,
                private readonly m_filterOptions: FilterOptionsExtended,
                public readonly summaryRow?: Models.TagPostureCloudAccountSummary[],
                public readonly definition?: Models.WorkloadDefinition)
    {
    }

    getLastUpdatedNotice(): string
    {
        const updatedDate = this.m_postureScopeInfo.createdAt || this.snapshot.created_at;
        const updatedAt   = MomentHelper.parse(updatedDate, "local");
        return `Posture computed on ${MomentHelper.friendlyFormatConciseUS(updatedAt, true)}`;
    }

    getLastUpdatedTimeMessage(): string
    {
        const updatedDate = this.m_postureScopeInfo.createdAt || this.snapshot.created_at;
        const currentTs   = new Date();
        const diffInMs    = currentTs.getTime() - updatedDate.getTime();
        return diffInMs > 24 * 60 * 60 * 1000 ? "Regenerate the posture to reflect the latest resource and tag coverage" : "";
    }

    async getRemediator(baselinesSvc: TagBaselineDefinitionVersionsService,
                        tagPosture: TagPostureService,
                        selectedCloudAccountId?: string): Promise<Lookup<TagRemediationHandler>>
    {
        const cloudAccountId = selectedCloudAccountId || this.cloudAccountId;
        const summary        = this.m_processedSummaries.get(cloudAccountId);

        if (!summary) return {};

        try
        {
            const baselineVersions    = summary.baselines.map((reference) => TagBaselineDefinitionVersionExtended.newIdentity(reference.baseline_version_id));
            const baselineVersionExts = await baselinesSvc.getExtendedBatch(baselineVersions);
            return TagRemediationHandler.generateHelperLookup(baselineVersionExts, this);
        }
        catch
        {
            return {};
        }
    }

    matchesFilter(filter: string): boolean
    {
        return UtilsService.stringMatchesRegex(this.workloadName || this.cloudAccountName || this.tenantName || this.accountId, filter, false);
    }

    matchesScope(scope: Models.TagBaselineScope): boolean
    {
        if (scope instanceof Models.TagBaselineScopeGlobal)
        {
            return true;
        }

        if (scope instanceof Models.TagBaselineScopeTenant)
        {
            return scope.tenant_ids?.includes(this.tenantId);
        }

        if (scope instanceof Models.TagBaselineScopeCloudAccount)
        {
            return scope.cloud_account_ids?.includes(this.cloudAccountId);
        }

        return false;
    }

    getCloudCoverage(cloud: Models.CloudServiceName): TagCoverageSummary
    {
        return this.m_cloudCoverages.get(cloud);
    }

    processCloudAccountSummary(summary: TagPostureCloudAccountSummaryExtended,
                               fromParent: boolean)
    {
        if (!this.m_processedSummaries.has(summary.model?.cloud_account_id))
        {
            this.m_processedSummaries.set(summary.model?.cloud_account_id, summary.model);

            this.processCloudHelper(summary.model?.cloud, fromParent);

            if (!fromParent)
            {
                const cloudSummary = this.m_cloudCoverages.get(summary.model?.cloud);
                cloudSummary.processCloudAccountSummary(summary, true);
            }

            this.m_postureScopeInfo.processCloudAccountSummary(summary);
            this.updateAssociatedNames();
            this.m_postureTime = this.getLastUpdatedNotice();

            const coverage: ResourceCoverage = summary.model?.resource_coverage ?? {
                valid_resource_count: 0,
                resource_count      : 0,
                tag_counts          : {
                    valid_count       : 0,
                    invalid_count     : 0,
                    missing_keys_count: 0
                }
            };

            let {
                    valid_resource_count: validResourceCount,
                    resource_count      : resourceCount
                } = coverage;

            let {
                    valid_count       : tagSummaryValidCount,
                    invalid_count     : tagSummaryInvalidCount,
                    missing_keys_count: tagSummaryMissingKeyCount
                } = coverage.tag_counts;

            if (summary?.summaryRow?.length > 0)
            {
                const cumulative = summary.summaryRow.reduce((acc,
                                                              cumulativeCoverage) =>
                                                             {
                                                                 acc.validResourceCount += cumulativeCoverage.resource_coverage.valid_resource_count;
                                                                 acc.resourceCount += cumulativeCoverage.resource_coverage.resource_count;
                                                                 acc.tagSummaryValidCount += cumulativeCoverage.resource_coverage.tag_counts.valid_count;
                                                                 acc.tagSummaryInvalidCount += cumulativeCoverage.resource_coverage.tag_counts.invalid_count;
                                                                 acc.tagSummaryMissingKeyCount += cumulativeCoverage.resource_coverage.tag_counts.missing_keys_count;
                                                                 return acc;
                                                             }, {
                                                                 validResourceCount       : 0,
                                                                 resourceCount            : 0,
                                                                 tagSummaryValidCount     : 0,
                                                                 tagSummaryInvalidCount   : 0,
                                                                 tagSummaryMissingKeyCount: 0
                                                             });

                ({
                    validResourceCount,
                    resourceCount,
                    tagSummaryValidCount,
                    tagSummaryInvalidCount,
                    tagSummaryMissingKeyCount
                } = cumulative);
            }
            const numTags = tagSummaryValidCount + tagSummaryInvalidCount + tagSummaryMissingKeyCount;
            this.incrementCoverageInfo(tagSummaryValidCount, numTags, validResourceCount, resourceCount);
        }
    }

    processCoverageSummary(summary: TagCoverageSummary,
                           fromParent: boolean): boolean
    {
        for (let [currId, currSummary] of summary.m_processedSummaries.entries())
        {
            if (this.m_processedSummaries.has(currId)) return false;
            this.m_processedSummaries.set(currId, currSummary);
        }

        for (let cloud of summary.m_associatedClouds)
        {
            this.processCloudHelper(cloud, fromParent);

            const cloudSpecificCoverage = summary.m_cloudCoverages.get(cloud);
            if (!fromParent && cloudSpecificCoverage)
            {
                const cloudSummary = this.m_cloudCoverages.get(cloud);
                cloudSummary.processCoverageSummary(cloudSpecificCoverage, true);
            }
        }

        this.m_postureScopeInfo.processScopeInfo(summary.m_postureScopeInfo);
        this.updateAssociatedNames();
        this.m_postureTime = this.getLastUpdatedNotice();

        this.incrementCoverageInfo(summary.m_validTags, summary.m_numTags, summary.m_coveredResources, summary.m_numResources);

        return true;
    }

    private processCloudHelper(cloud: Models.CloudServiceName,
                               fromParent: boolean)
    {
        if (!this.m_associatedClouds.has(cloud))
        {
            this.m_associatedClouds.add(cloud);

            if (!fromParent) this.m_cloudCoverages.set(cloud, new TagCoverageSummary(this.snapshot, this.m_filterOptions));
        }
    }

    private updateAssociatedNames()
    {
        if (this.m_tenantOption?.id === this.tenantId) return;

        this.m_tenantOption = this.tenantId ? this.m_filterOptions.tenantOptions.find((tenantOption) => tenantOption.id === this.tenantId) : null;
    }

    private incrementCoverageInfo(validTags: number,
                                  numTags: number,
                                  coveredResources: number,
                                  numResources: number)
    {
        this.m_validTags += validTags;
        this.m_numTags += numTags;
        this.m_coveredResources += coveredResources;
        this.m_numResources += numResources;
    }
}

class TagScopeInfo
{
    public organizationId: string;
    public tenantId: string;
    public cloudAccountId: string;
    public cloudAccountName: string;
    public workloadName: string;
    public createdAt: Date;
    public workloadId: string;

    public processCloudAccountSummary(summary: TagPostureCloudAccountSummaryExtended)
    {
        this.checkUpdateUniqueProps(summary.model?.account_id,
                                    summary.model?.tenant_id,
                                    summary.model?.cloud_account_id,
                                    summary.model?.cloud_account_name,
                                    summary.model?.created_at,
                                    summary?.workloadRow?.name,
                                    summary?.workloadRow?.sysId);
    }

    public processScopeInfo(other: TagScopeInfo)
    {
        this.checkUpdateUniqueProps(other.organizationId, other.tenantId, other.cloudAccountId, other.cloudAccountName, other.createdAt, other.workloadName);
    }

    private checkUpdateUniqueProps(organizationId: string,
                                   tenantId: string,
                                   cloudAccountId: string,
                                   cloudAccountName: string,
                                   createdAt: Date,
                                   workloadName?: string,
                                   workloadId?: string)
    {
        this.checkUpdateUnique("organizationId", organizationId);
        this.checkUpdateUnique("tenantId", tenantId);
        this.checkUpdateUnique("cloudAccountId", cloudAccountId);
        this.checkUpdateUnique("cloudAccountName", cloudAccountName);
        this.checkUpdateUnique("workloadName", workloadName);
        this.checkUpdateUnique("createdAt", createdAt);
        this.checkUpdateUnique("workloadId", workloadId);
    }

    private checkUpdateUnique(prop: keyof TagScopeInfo,
                              value: any)
    {
        if (this.hasOwnProperty(prop))
        {
            if (this[prop] !== value) this[prop] = null;
        }
        else
        {
            this[prop] = value;
        }
    }
}

export enum TagPostureScope
{
    CloudAccounts = "cloudaccounts",
    Organizations = "organizations",
    Tenants       = "tenants",
    Workloads     = "workloads"
}

export class TagRemediationState
{
    get statusFriendly(): string
    {
        switch (this.status)
        {
            case TagRemediationStatus.Untaggable:
                return "";

            case TagRemediationStatus.NotSynced:
                return "Not Synced";

            case TagRemediationStatus.Pending:
                return "Pending";

            case TagRemediationStatus.Synced:
                return "Synced";

            default:
                return "Unknown";
        }
    }

    get statusStyles(): Lookup<string>
    {
        return {"color": TagRemediationState.getStatusColor(this.status)};
    }

    constructor(public readonly status: TagRemediationStatus,
                public readonly error: string)
    {}

    moreSevereThan(other: TagRemediationState): boolean
    {
        return UtilsService.compareNumbers(this.status, other.status, true) < 0;
    }

    public static getStatusColor(state: TagRemediationStatus): string
    {
        switch (state)
        {
            case TagRemediationStatus.Untaggable:
                return "rgba(0, 0, 0, 0.5)";

            case TagRemediationStatus.NotSynced:
                return ChartColorUtilities.getColorById("Severity Colors", "critical").color;

            case TagRemediationStatus.Pending:
                return ChartColorUtilities.getColorById("Severity Colors", "high").color;

            case TagRemediationStatus.Synced:
                return ChartColorUtilities.getColorById("Severity Colors", "low").color;
        }
    }
}

export class TagRemediationStateOverall
{
    public readonly overallState: TagRemediationState;
    public readonly stateByTag: Lookup<TagRemediationState> = {};
    public readonly unsyncedValues: Lookup<string>          = {};

    constructor(remediationData: Lookup<Models.TagRemediationStatus>,
                summary: Models.TagPostureResourceSummary)
    {
        if (remediationData && summary?.supports_remediation)
        {
            const tagStatus = remediationData[summary.resource_id];

            if (!tagStatus)
            {
                this.overallState = null;
                return;
            }

            let mostSevere = new TagRemediationState(TagRemediationStatus.Synced, "");

            if (LookupHelpers.keys(remediationData).length)
            {
                let state: TagRemediationState;
                let unsyncedValue: string;

                for (let i = 0; i < tagStatus?.tags.length; i++)
                {
                    if (!this.stateByTag[i])
                    {
                        if (!state)
                        {
                            unsyncedValue = tagStatus.tags[i].value;
                            switch (tagStatus.status)
                            {
                                case "completed":
                                    state = new TagRemediationState(TagRemediationStatus.Synced, "");
                                    break;

                                case "pending":
                                case "in_progress":
                                    state = new TagRemediationState(TagRemediationStatus.Pending, "");
                                    break;

                                default:
                                    state         = new TagRemediationState(TagRemediationStatus.NotSynced, tagStatus.status);
                                    unsyncedValue = null;
                                    break;
                            }

                            if (state.moreSevereThan(mostSevere)) mostSevere = state;
                        }

                        this.stateByTag[i] = state;
                    }

                    if (!this.unsyncedValues[i] && unsyncedValue)
                    {
                        this.unsyncedValues[i] = tagStatus.tags[i].value;
                    }
                }
            }

            this.overallState = mostSevere;
        }
        else
        {
            this.overallState = new TagRemediationState(TagRemediationStatus.Untaggable, "");
        }
    }
}

export enum TagRemediationStatus
{
    Untaggable,
    NotSynced,
    Pending,
    Synced
}

export interface WorkloadSummary
{
    definition: Models.WorkloadDefinition;
    postureSummary: Models.TagPostureCloudAccountSummary[];
}

export interface CoverageTypeSummary
{
    tag: string[];
    cloudAccountId?: string;
}

export interface ScoreRequestContext
{
    workloadId?: string;
    cloudName?: Models.CloudServiceName;
    searchText?: string;
    cloudAccountId: string;
    snapshotId: string;
    query: Models.TagPostureContext;
}

interface ResourceCoverage
{
    valid_resource_count: number;
    resource_count: number;
    tag_counts: {
        valid_count: number;
        invalid_count: number;
        missing_keys_count: number;
    };
}

export class TagPostureCloudAccountSummaryExtended
{
    constructor(public readonly model: Models.TagPostureCloudAccountSummary,
                public readonly workloadRow?: Models.WorkloadDefinition,
                public readonly summaryRow?: Models.TagPostureCloudAccountSummary[])
    {}
}
